import cx from "classnames";
import { ContentState, convertFromRaw, convertToRaw, Editor, EditorState, Modifier } from "draft-js";
import "draft-js/dist/Draft.css";
import { getEntityAtCursor, getVisibleSelectionRectFixed, linkDecorator, selectionIsLink } from "lib/draftUtils";
import { stripNewlines } from "lib/utils";
import _ from "lodash";
import React from "react";
import { findDOMNode } from "react-dom";
import { InlineToolbar } from "../RichTextEditor/InlineToolbar";
import styles from "./SingleLineEditor.scss";

// Single-line, plaintext editor that wraps draft-js. Note that because of the
// way draft.js works, this is analagous to an *uncontrolled* <input> - you can
// read values out, but don't expect to pass values in other than defaultValue
export class SingleLineEditor extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      inlineToolbarPos: { left: 0, top: 0 },
      frozenSelection: null,
    };

    const isRichText = (str) => {
      try {
        JSON.parse(str);
      } catch (e) {
        return false;
      }
      return true;
    };

    if (props.defaultValue) {
      var content = isRichText(props.defaultValue)
        ? convertFromRaw(JSON.parse(props.defaultValue))
        : ContentState.createFromText(props.defaultValue);
      this.state.editorState = EditorState.createWithContent(content, linkDecorator);
    } else {
      this.state.editorState = EditorState.createEmpty(linkDecorator);
    }
  }

  handleReturn = () => {
    if (this.props.onReturn) {
      this.props.onReturn();
    }
    return "handled"; // prevents the user from inserting line breaks with return key
  };

  onChange = (editorState, cb) => {
    this.setState({ editorState }, cb);
    const { onChange } = this.props;
    // only fire props.onChange if the content actually changed
    if (onChange && this._lastContent !== editorState.getCurrentContent()) {
      // subtitle gets special handling to preserve formatting
      if (this.props.type === "subtitle" || this.props.type === "caption") {
        onChange(JSON.stringify(convertToRaw(editorState.getCurrentContent())));
      } else {
        onChange(editorState.getCurrentContent().getPlainText());
      }
    }
    this._lastContent = editorState.getCurrentContent();
  };

  handlePastedText = (text, html) => {
    const { editorState } = this.state;
    const contentState = editorState.getCurrentContent();
    const replaced = Modifier.replaceText(contentState, editorState.getSelection(), stripNewlines(text, " "));
    const newEditorState = EditorState.push(editorState, replaced, "insert-fragment");
    this.onChange(newEditorState);
    return true;
  };

  handleBeforeInput = (chars) => {
    // if the user inputs a space character at the end of a link, break out of
    // the link.
    const { editorState } = this.state;
    const selection = editorState.getSelection();
    if (chars === " " && selection.isCollapsed() && selectionIsLink(editorState)) {
      const trailingEntity = getEntityAtCursor(editorState, "trailing");
      if (trailingEntity && trailingEntity.getType() === "LINK") {
        return "not-handled";
      }
      const newContent = Modifier.insertText(
        editorState.getCurrentContent(),
        editorState.getSelection(),
        chars,
        null,
        null
      );
      this.onChange(EditorState.push(editorState, newContent, "insert-characters"));
      return "handled";
    }
    return "not-handled";
  };

  componentDidUpdate(prevProps) {
    this.setInlineToolbarPosition();
  }

  setInlineToolbarPosition() {
    const selectionPosition = getVisibleSelectionRectFixed(window);
    const anchorNode = document.getSelection().anchorNode;
    // Firefox throws errors on us even trying to access the nodeType property in
    // <input> elements, since the input area is implemented as an 'anonymous div'
    //
    // the only workaround seems to be to wrap this in a try/catch block
    // https://github.com/facebook/react/blob/master/src/renderers/dom/shared/ReactDOMSelection.js#L44-L58
    let contentEditable;
    try {
      contentEditable =
        anchorNode &&
        (anchorNode.nodeType === window.Node.TEXT_NODE
          ? anchorNode.parentNode.isContentEditable
          : anchorNode.isContentEditable);
    } catch (e) {
      return;
    }

    // sanity checks
    if (
      !contentEditable ||
      !this._container || // need a live editor ref for this to work
      !selectionPosition // no selection
    ) {
      return;
    }
    const { inlineToolbarPos } = this.state;
    const newInlineToolbarPos = {
      top: selectionPosition.top + window.scrollY,
      left: (selectionPosition.left + selectionPosition.right) / 2 + window.scrollX, // problem: this results in an element that is incorrectly positioned by -paddingLeft px in the x axis. WAT.
    };

    if (!_.isEqual(newInlineToolbarPos, inlineToolbarPos)) {
      this.setState({ inlineToolbarPos: newInlineToolbarPos });
    }
  }

  // This method is purely visual - it persists a selection visually so that
  // it can still serve as a UI cue when the editor loses focus (i.e. when the
  // user is editing a link). If you want to prevent the selection from
  // changing, do your thing in onChange...
  freezeSelection = () => {
    const selectionRects = window.getSelection().getRangeAt(0).getClientRects();
    const node = findDOMNode(this._container);
    const editorOffset = node.getBoundingClientRect();
    const frozenSelection = Array.from(selectionRects).map((rect) => ({
      top: rect.top - editorOffset.top,
      left: rect.left - editorOffset.left,
      width: rect.right - rect.left,
      height: rect.bottom - rect.top,
    }));
    this.setState({ frozenSelection });
  };

  unfreezeSelection = () => {
    this.setState({ frozenSelection: null });
  };

  onClickContainer = (e) => {
    // don't mess with focus if selection is frozen
    if (this.state.frozenSelection) {
      return;
    }

    // if we've already got focus, ignore
    if (this.state.editorState.getSelection().getHasFocus()) {
      return;
    }

    // ignore if it's an atomic block interaction
    const hasAtomicAncestor = (node) => {
      if (!node) {
        return false;
      }
      if (node.tagName === "FIGURE") {
        return true;
      }
      if (node.classList.contains("DraftEditor-root")) {
        return false;
      }
      return hasAtomicAncestor(node.parentElement);
    };

    // if the click is below the draft editor node, focus the last block
    if (e.clientY > findDOMNode(this._editor).getBoundingClientRect().bottom) {
      this.props.onChange(EditorState.moveFocusToEnd(this.props.editorState), () => this.focusEditor());
      return;
    }

    if (!hasAtomicAncestor(e.target)) {
      this.focusEditor();
    }
  };

  focusEditor = () => {
    const { editorState } = this.state;
    const hasFocus = editorState.getSelection().getHasFocus();
    if (this._editor && !hasFocus) {
      // this functionality can cause particularly hard-to-diagnose bugs, so
      // I'm keeping the console.log & console.trace around for now
      console.log("focusEditor");
      window.console.trace();
      this._editor.focus();
    }
  };

  focus = () => {
    this._editor && this._editor.focus();
  };

  blur = () => {
    this._editor && this._editor.blur();
  };

  setRef = (editor) => {
    this._editor = editor;
  };

  render() {
    const { editorState, inlineToolbarPos, frozenSelection } = this.state;
    const { placeholder, textAlignment, spellCheck, onFocus, onBlur, className, readOnly, lineClamp, plaintext } =
      this.props;
    return (
      <div
        className={cx(styles.SingleLineEditor, readOnly && lineClamp && styles.clamp, className)}
        ref={(node) => {
          this._container = node;
        }}
      >
        <Editor
          readOnly={readOnly}
          ref={this.setRef}
          onFocus={onFocus}
          onBlur={onBlur}
          spellCheck={spellCheck}
          editorState={editorState}
          onChange={this.onChange}
          handleReturn={this.handleReturn}
          handlePastedText={this.handlePastedText}
          handleBeforeInput={this.handleBeforeInput}
          placeholder={placeholder}
          textAlignment={textAlignment}
        />
        {!plaintext && (
          <InlineToolbar
            editorState={editorState}
            onChange={this.onChange}
            position={inlineToolbarPos}
            selectionIsFrozen={!!frozenSelection}
            freezeSelection={this.freezeSelection}
            unfreezeSelection={this.unfreezeSelection}
            restoreFocus={this.focusEditor}
          />
        )}
      </div>
    );
  }
}
